// app/frontend/components/Cards/ContenPostCard.tsx

import React from 'react';
import { Card, CardSection, Image, Text, Badge, Group, AspectRatio, Button } from '@mantine/core';
import { Link } from '@inertiajs/react';
import { IconPencil, IconMicrophone, IconPlayerPlay, IconLibraryPhoto } from '@tabler/icons-react';

export interface DanceStyle {
  id: number;
  name: string;
}

export interface Post {
  slug: string;
  title: string;
  image_url?: string | null;
  published_at?: string;
  dance_styles?: DanceStyle[];
  postTypeIcons?: React.ReactNode;
  post_types?: { id: number; name: string }[];
}

interface PostContentCardProps {
  post: Post;
  baseImage?: string;
  onCardClick?: (post: Post) => void;
}

const PostContentCard: React.FC<PostContentCardProps> = ({ post, baseImage, onCardClick }) => {
  const { slug, title, image_url, published_at, dance_styles = [], postTypeIcons, post_types = [] } = post;

  const postTypeIconMap: Record<string, React.ReactNode> = {
    Blog: <IconPencil size={12} />,
    Podcast: <IconMicrophone size={12} />,
    Video: <IconPlayerPlay size={12} />,
    Carousel: <IconLibraryPhoto size={12} />,
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (onCardClick) {
      e.preventDefault(); // Prevent navigation
      onCardClick(post);
    }
  };

  // format published date
  const publishedDate = published_at
    ? new Date(published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <Card
      component={Link}
      href={`/posts/${slug}`}
      shadow="sm"
      p="md"
      radius="md"
      withBorder
      onClick={handleCardClick}
      style={{
        backgroundColor: '#17171750',
        borderColor: '#5c5c5c',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      {/* Featured image or fallback */}
      <CardSection style={{ overflow: 'hidden', position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <AspectRatio ratio={16 / 9}>
          {image_url ? (
            <Image src={image_url} alt={title} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#2a2a2a',
                color: '#d1d1d1',
                width: '100%',
                height: '100%',
                fontFamily: 'Poppins',
              }}
            >
              No featured image attached.
            </div>
          )}
        </AspectRatio>
        {post_types && post_types.length > 0 && (
          <Group gap="xs" style={{ position: 'absolute', top: 8, left: 8 }}>
            {post_types.map((type) => (
              <Badge
                key={type.id}
                variant="filled"
                color="rgba(0, 0, 0, 0.63)"
                size="md"
                className="poppins"
                leftSection={postTypeIconMap[type.name]}
              >
                {type.name}
              </Badge>
            ))}
          </Group>
        )}
      </CardSection>

      {/* Title and icons */}
      <Group align="center" gap="4px" mt="8px">
        {postTypeIcons}
        <Text fw={700} lineClamp={2} className="poppins" style={{ color: '#f2f2f2', lineHeight: 1.2 }}>
          {title}
        </Text>
      </Group>

      {/* Published date */}
      {publishedDate && (
        <Text size="sm" className="poppins mt-1" style={{ fontSize: 12, color: '#d1d1d1' }}>
          Published: {publishedDate}
        </Text>
      )}

      {/* Dance style badges */}
      {dance_styles.length > 0 && (
        <Group gap="4px" mt="xs" mb="md" wrap="wrap">
          {dance_styles.slice(0, 3).map((style) => (
            <Badge
              key={style.id}
              variant="filled"
              color="#EA580C"
              radius="xl"
              size="xs"
              className="poppins"
              style={{ backgroundColor: '#EA580C', textTransform: 'none' }}
            >
              {style.name}
            </Badge>
          ))}
          {dance_styles.length > 3 && (
            <Text size="xs" className="poppins" style={{ color: '#d1d1d1', fontSize: 12 }}>
              + {dance_styles.length - 3}
            </Text>
          )}
        </Group>
      )}
    </Card>
  );
};

export default PostContentCard;
